import {
  autoUpdate,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react';
import {
  ArrowPathIcon,
  CheckIcon,
  ClipboardIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { blobSinkActionService } from '@/services/api/blobSinkActionService';
import { BlobSinkAction } from '@/services/types/blob-types';
import { getExternalSystemDisplayName } from '@/utils/externalSystemUtils';

import { formatDateString } from '../utils/fileUtils';
import { getSinkMetadataDisplayRows } from '../utils/sinkMetadataDisplay';

const getTargetSystemName = getExternalSystemDisplayName;

interface SinkActionIndicatorProps {
  sinkAction: BlobSinkAction;
  matterId: string;
}

const POLLING_STATUSES = [
  'PENDING',
  'PROCESSING',
  'WAITING_FOR_PUSH_WINDOW',
  'READY_TO_PUSH',
  'PUSHING',
  'AWAITING_APPROVAL',
];

export const SinkActionIndicator: React.FC<SinkActionIndicatorProps> = ({
  sinkAction: initialSinkAction,
  matterId,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isReasoningExpanded, setIsReasoningExpanded] = useState(false);
  const [shouldPoll, setShouldPoll] = useState(
    POLLING_STATUSES.includes(initialSinkAction.status),
  );

  const [approveSinkAction, { isLoading: isApproving }] =
    blobSinkActionService.useBlobSinkActionsApproveMutation();
  const [rejectSinkAction, { isLoading: isRejecting }] =
    blobSinkActionService.useBlobSinkActionsRejectMutation();
  const [repushSinkAction, { isLoading: isRepushing }] =
    blobSinkActionService.useBlobSinkActionsRepushMutation();

  // Poll for updates when status is in progress
  const { data: polledSinkAction } =
    blobSinkActionService.useBlobSinkActionsRetrieveQuery(
      {
        matterId,
        blobId: initialSinkAction.blob_id,
        actionId: initialSinkAction.id,
      },
      {
        pollingInterval: shouldPoll ? 5000 : 0,
        skip: !shouldPoll,
        skipPollingIfUnfocused: true,
      },
    );

  // Stop polling when status changes to a non-polling status
  useEffect(() => {
    if (
      polledSinkAction &&
      !POLLING_STATUSES.includes(polledSinkAction.status)
    ) {
      setShouldPoll(false);
    }
  }, [polledSinkAction]);

  // Use polled data merged with initial (to preserve fields like modified_at)
  const sinkAction: BlobSinkAction = polledSinkAction
    ? { ...initialSinkAction, ...polledSinkAction }
    : initialSinkAction;

  const isLoading = isApproving || isRejecting || isRepushing;

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'bottom-start',
    middleware: [offset(8), flip(), shift({ padding: 10 })],
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  const handleApprove = async () => {
    try {
      await approveSinkAction({
        matterId,
        blobId: sinkAction.blob_id,
        actionId: sinkAction.id,
      }).unwrap();
      toast.success('Sink action approved');
      setIsOpen(false);
    } catch (error: any) {
      const errorMessage =
        error?.data?.detail ||
        error?.data?.message ||
        error?.message ||
        'Failed to approve sink action';
      toast.error(errorMessage);
    }
  };

  const handleReject = async () => {
    try {
      await rejectSinkAction({
        matterId,
        blobId: sinkAction.blob_id,
        actionId: sinkAction.id,
        rejectActionRequest: {},
      }).unwrap();
      toast.success('Sink action rejected');
      setIsOpen(false);
    } catch (error: any) {
      const errorMessage =
        error?.data?.detail ||
        error?.data?.message ||
        error?.message ||
        'Failed to reject sink action';
      toast.error(errorMessage);
    }
  };

  const handleRetry = async () => {
    const targetName = getTargetSystemName(sinkAction.target_system);
    try {
      await repushSinkAction({
        matterId,
        blobId: sinkAction.blob_id,
        actionId: sinkAction.id,
      }).unwrap();
      toast.success(`Retrying push to ${targetName}`);
      setIsOpen(false);
    } catch (error: any) {
      const errorMessage =
        error?.data?.detail ||
        error?.data?.message ||
        error?.message ||
        'Failed to retry push';
      toast.error(errorMessage);
    }
  };

  const getStatusConfig = () => {
    const targetName = getTargetSystemName(sinkAction.target_system);
    switch (sinkAction.status) {
      case 'FAILED':
        return {
          color: 'bg-red-400',
          shadowColor: 'shadow-red-400/50',
          label: 'Failed',
          labelColor: 'text-red-600',
        };
      case 'AWAITING_APPROVAL':
        return {
          color: 'bg-amber-400',
          shadowColor: 'shadow-amber-400/50',
          label: 'Awaiting approval',
          labelColor: '',
        };
      case 'PENDING':
        return {
          color: 'bg-blue-400',
          shadowColor: 'shadow-blue-400/50',
          label: 'Pending',
          labelColor: '',
        };
      case 'PROCESSING':
        return {
          color: 'bg-blue-400',
          shadowColor: 'shadow-blue-400/50',
          label: 'Processing',
          labelColor: '',
        };
      case 'READY_TO_PUSH':
        return {
          color: 'bg-blue-400',
          shadowColor: 'shadow-blue-400/50',
          label: 'Ready to push',
          labelColor: '',
        };
      case 'WAITING_FOR_PUSH_WINDOW':
        return {
          color: 'bg-blue-400',
          shadowColor: 'shadow-blue-400/50',
          label: 'Waiting for push window',
          labelColor: '',
        };
      case 'PUSHING':
        return {
          color: 'bg-blue-400',
          shadowColor: 'shadow-blue-400/50',
          label: 'Pushing',
          labelColor: '',
        };
      case 'PUSHED':
        return {
          color: 'bg-green-600',
          shadowColor: 'shadow-green-600/30',
          label: `Pushed to ${targetName}`,
          labelColor: 'text-green-700',
        };
      case 'ALREADY_EXISTS':
        return {
          color: 'bg-green-600',
          shadowColor: 'shadow-green-600/30',
          label: `Already in ${targetName}`,
          labelColor: 'text-green-700',
        };
      case 'REMOVED':
        return {
          color: 'bg-slate-400',
          shadowColor: 'shadow-slate-400/30',
          label: `Removed from ${targetName}`,
          labelColor: 'text-slate-600',
        };
      default:
        return null;
    }
  };

  const statusConfig = getStatusConfig();

  // Don't render for statuses that shouldn't show an indicator
  if (!statusConfig) {
    return null;
  }

  // Hide failed indicators older than 3 days
  if (sinkAction.status === 'FAILED') {
    const modifiedDate = new Date(sinkAction.modified_at);
    const threeDaysAgo = new Date();
    threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
    if (modifiedDate < threeDaysAgo) {
      return null;
    }
  }

  // Hide pushed/already exists/removed indicators older than 3 hours
  if (
    sinkAction.status === 'PUSHED' ||
    sinkAction.status === 'ALREADY_EXISTS' ||
    sinkAction.status === 'REMOVED'
  ) {
    const modifiedDate = new Date(sinkAction.modified_at);
    const threeHoursAgo = new Date();
    threeHoursAgo.setHours(threeHoursAgo.getHours() - 3);
    if (modifiedDate < threeHoursAgo) {
      return null;
    }
  }

  const isAwaitingApproval = sinkAction.status === 'AWAITING_APPROVAL';
  const isFailed = sinkAction.status === 'FAILED';
  const isRemoved = sinkAction.status === 'REMOVED';
  const canRetry = isFailed || isRemoved;
  const metadataRows = getSinkMetadataDisplayRows(sinkAction);

  return (
    <>
      <button
        ref={refs.setReference}
        type="button"
        className="flex items-center justify-center hover:opacity-70 transition-opacity -ml-1.5 -mt-1.5 p-1"
        onClick={(e) => e.stopPropagation()}
        title={statusConfig.label}
        {...getReferenceProps()}
      >
        <span
          className={`block w-1.5 h-1.5 rounded-full ${statusConfig.color} shadow-[0_0_3px_1px] ${statusConfig.shadowColor}`}
        />
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            role="dialog"
            className="z-[9999] w-72 bg-white border border-slate-200 rounded-lg shadow-lg p-3"
          >
            <div className="space-y-2 group/panel">
              {/* Status header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span
                    className={`block w-2 h-2 rounded-full ${statusConfig.color}`}
                  />
                  <span
                    className={`font-medium text-sm ${statusConfig.labelColor}`}
                  >
                    {statusConfig.label}
                  </span>
                </div>
                {sinkAction.last_workflow_run_id && (
                  <button
                    type="button"
                    className="opacity-0 group-hover/panel:opacity-100 p-1 text-slate-300 hover:text-slate-500 transition-opacity"
                    onClick={() => {
                      navigator.clipboard.writeText(
                        sinkAction.last_workflow_run_id!,
                      );
                    }}
                    title="Copy workflow ID"
                  >
                    <ClipboardIcon className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              {/* Failed timestamp */}
              {isFailed && (
                <div className="text-xs">
                  <span className="text-muted-foreground">Failed at: </span>
                  <span className="font-medium">
                    {formatDateString(sinkAction.modified_at, 'short')}
                  </span>
                </div>
              )}

              {/* File name */}
              {sinkAction.target_name && (
                <div className="text-xs">
                  <span className="text-muted-foreground">File: </span>
                  <span className="font-medium">{sinkAction.target_name}</span>
                </div>
              )}

              {/* Destination path */}
              {sinkAction.target_path != null && (
                <div className="text-xs">
                  <span className="text-muted-foreground">Destination: </span>
                  <span className="font-medium">{sinkAction.target_path}</span>
                </div>
              )}

              {metadataRows.map((row) => (
                <div key={row.label} className="text-xs">
                  <span className="text-muted-foreground">{row.label}: </span>
                  <span className="font-medium">{row.value}</span>
                </div>
              ))}

              {/* Confidence score */}
              {sinkAction.target_confidence != null && (
                <div className="text-xs">
                  <span className="text-muted-foreground">Confidence: </span>
                  <span className="font-medium">
                    {Math.round(sinkAction.target_confidence * 100)}%
                  </span>
                </div>
              )}

              {/* Reasoning */}
              {sinkAction.target_reasoning && (
                <div className="text-xs">
                  <span className="text-muted-foreground">Reasoning: </span>
                  {sinkAction.target_reasoning.length > 100 ? (
                    <>
                      <span>
                        {isReasoningExpanded
                          ? sinkAction.target_reasoning
                          : `${sinkAction.target_reasoning.slice(0, 100)}...`}
                      </span>
                      <button
                        type="button"
                        className="ml-1 text-blue-600 hover:text-blue-700 hover:underline"
                        onClick={() =>
                          setIsReasoningExpanded(!isReasoningExpanded)
                        }
                      >
                        {isReasoningExpanded ? 'show less' : 'show more'}
                      </button>
                    </>
                  ) : (
                    <span>{sinkAction.target_reasoning}</span>
                  )}
                </div>
              )}

              {/* Error message (only for failed) */}
              {isFailed &&
                (!!sinkAction.metadata?.error_message ||
                  !!sinkAction.metadata?.error) && (
                  <div className="text-xs">
                    <span className="text-muted-foreground">Error: </span>
                    <span className="font-medium text-red-600">
                      {String(
                        (sinkAction.metadata?.error_message ||
                          sinkAction.metadata?.error) ??
                          '',
                      )}
                    </span>
                  </div>
                )}

              {/* Approve/Reject actions (only for awaiting approval) */}
              {isAwaitingApproval && (
                <div className="flex items-center gap-2 pt-2 mt-1 border-t">
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1 h-8 border-slate-200 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-200"
                    onClick={handleApprove}
                    disabled={isLoading}
                  >
                    <CheckIcon className="w-3.5 h-3.5 mr-1.5" />
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1 h-8 border-slate-200 text-rose-700 hover:bg-rose-50 hover:border-rose-200"
                    onClick={handleReject}
                    disabled={isLoading}
                  >
                    <XMarkIcon className="w-3.5 h-3.5 mr-1.5" />
                    Reject
                  </Button>
                </div>
              )}

              {/* Retry action (only for failed or removed) */}
              {canRetry && (
                <div className="flex items-center gap-2 pt-2 mt-1 border-t">
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full h-8 border-slate-200 text-blue-700 hover:bg-blue-50 hover:border-blue-200"
                    onClick={handleRetry}
                    disabled={isLoading}
                  >
                    <ArrowPathIcon className="w-3.5 h-3.5 mr-1.5" />
                    Retry Push
                  </Button>
                </div>
              )}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};
